// usePusherChannel — subscribe to a private channel and collect typed events.
// Server→client push is Pusher ONLY (doctrine 1.2): no SSE, no polling. Works
// against Soketi locally and real Pusher in cloud (same client, different host).

import { useEffect, useRef, useState } from "react";

import { acquireChannel, emitPusherHealth } from "./pusher-connection";

export interface PusherChannelConfig {
  key: string;
  host: string;
  port: number;
  useTls: boolean;
  /** Required by pusher-js even for self-hosted Soketi; ignored when host is set. */
  cluster?: string;
  /** The /api/pusher/auth endpoint that signs private-channel subscriptions. */
  authEndpoint: string;
  /** Headers the auth POST must carry (stub identity). */
  authHeaders?: Record<string, string>;
  /**
   * Close the WebSocket after the tab has been hidden this many ms, then reopen
   * it (re-subscribing + reconciling via `onReconnect`) when it becomes visible
   * again. Idle/backgrounded tabs otherwise hold their socket open indefinitely —
   * pusher-js answers the server's liveness pings, so the server never reaps them —
   * inflating the concurrent-connection count Pusher bills and caps on, out of
   * proportion to actually-active users. Defaults to 30s; set to `0` to disable
   * (e.g. an operator spectate/debug tab that must stay hot). The policy is
   * per-connection, taken from the first consumer that opens the shared socket.
   */
  idleDisconnectMs?: number;
}

/**
 * Subscribe to `channelName` (or nothing when null) and return events received on
 * any of `eventNames`, in arrival order. Re-subscribes when the channel changes;
 * releases its lease on the shared, ref-counted connection (see
 * `./pusher-connection`) on unmount.
 *
 * `onReconnect` (optional) fires on the 2nd+ `pusher:subscription_succeeded`, not
 * the first. Pusher does NOT replay events published while the socket was down (a
 * network blip, an idle timeout, the idle-tab disconnect), so a consumer uses this
 * to re-read authoritative state over REST.
 *
 * `onSubscriptionError` (optional) fires on EVERY `pusher:subscription_error`,
 * including the first subscribe attempt, which `onReconnect` skips. Without it, an
 * initial auth refusal leaves the consumer silently receiving nothing.
 *
 * Both callbacks are passed via refs, so changing one never re-subscribes.
 */
export function usePusherChannel<E>(
  channelName: string | null,
  eventNames: readonly string[],
  config: PusherChannelConfig,
  onReconnect?: () => void,
  onSubscriptionError?: () => void,
): E[] {
  const [events, setEvents] = useState<E[]>([]);
  const headersKey = JSON.stringify(config.authHeaders ?? {});
  const namesKey = eventNames.join(",");

  // Keep the latest callback without re-running the subscribe effect (which would
  // tear down + rebuild the connection on every render that passes a new closure).
  const onReconnectRef = useRef(onReconnect);
  onReconnectRef.current = onReconnect;
  const onSubscriptionErrorRef = useRef(onSubscriptionError);
  onSubscriptionErrorRef.current = onSubscriptionError;

  useEffect(() => {
    setEvents([]);
    if (!channelName) return;

    // Join (or create) the shared, ref-counted connection + channel. This hook
    // never constructs its own pusher-js instance — that would open a redundant
    // socket per consumer to the same channel.
    const lease = acquireChannel(config, channelName);
    const channel = lease.channel;
    const handler = (data: E): void => setEvents((prev) => [...prev, data]);
    for (const name of eventNames) channel.bind(name, handler);

    // Reconcile after a RE-subscribe: pusher-js re-subscribes every channel and
    // re-fires `pusher:subscription_succeeded` on each reconnect. The FIRST one is
    // the initial subscribe (the consumer's own bootstrap already loaded state);
    // every one AFTER it means the socket dropped and came back, so events may have
    // been missed in the gap — let the consumer re-read. A consumer that joined an
    // ALREADY-subscribed shared channel (late joiner) has effectively already had
    // its initial subscribe, so seed `subscribedOnce` true and treat its next
    // success as a reconnect.
    let subscribedOnce = lease.wasAlreadySubscribed;
    const onSubscribed = (): void => {
      if (subscribedOnce) onReconnectRef.current?.();
      subscribedOnce = true;
    };
    channel.bind("pusher:subscription_succeeded", onSubscribed);

    // Surface a failed (re-)subscribe loudly (doctrine 1.5). A RE-subscribe can fail
    // auth (e.g. an SSO cookie that expired while the tab was hidden), so after the
    // first success also nudge the consumer to re-read REST, mirroring
    // `onSubscribed`'s gate. Not on an initial failure: that would double-read on
    // bootstrap.
    const onSubscriptionError = (status: unknown): void => {
      console.error("[usePusherChannel] subscription_error", channelName, status);
      emitPusherHealth({ kind: "subscription_error", detail: channelName ?? undefined });
      if (subscribedOnce) onReconnectRef.current?.();
      onSubscriptionErrorRef.current?.();
    };
    channel.bind("pusher:subscription_error", onSubscriptionError);

    return () => {
      channel.unbind("pusher:subscription_succeeded", onSubscribed);
      channel.unbind("pusher:subscription_error", onSubscriptionError);
      for (const name of eventNames) channel.unbind(name, handler);
      lease.release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    channelName,
    config.key,
    config.host,
    config.port,
    config.useTls,
    config.cluster,
    config.authEndpoint,
    config.idleDisconnectMs,
    headersKey,
    namesKey,
  ]);

  return events;
}
